import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Package,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Upload,
  X,
  HelpCircle,
  MessageSquare
} from 'lucide-react';
import { servicesPageAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  ServicesPageContainer,
  FloatingElement,
  MainContent,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormSection,
  SectionTitle,
  InputGroup,
  Label,
  Input,
  TextArea,
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  ActionButton,
  Grid,
  ItemCard,
  ItemHeader,
  ItemNumber,
  ImageUploadContainer,
  ImagePreview,
  ImageUploadButton,
  RemoveImageButton,
  TestimonialCard,
  TestimonialImage,
  FAQCard,
  AddButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  PreviewSection,
  PreviewTitle,
  PreviewCard,
  Badge,
  EmptyState,
  EmptyStateText
} from './ServicesPage.styles';

const ServicesPage = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    page_title: '',
    page_paragraph: '',
    what_we_offer_title: '',
    what_we_offer_paragraph: '',
    what_we_offer_items: ['', '', '', '', '', ''], // Strictly 6
    how_it_works_title: '',
    how_it_works_paragraph: '',
    how_it_works_steps: [], // Array of strings
    benefits_title: '',
    benefits_items: ['', '', '', '', ''], // Max 5
    benefits_image: null,
    benefits_image_preview: null,
    use_cases_title: '',
    use_cases_paragraph: '',
    use_cases_items: ['', '', '', '', '', ''], // Max 6
    testimonials: [], // Array with message, photo, name, role
    faqs_title: '',
    faqs: [] // Array with question, answer
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadServicesPage();
  }, []);

  const loadServicesPage = async () => {
    try {
      setLoading(true);
      const response = await servicesPageAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setExistingData(data);
        setFormData({
          page_title: data.page_title || '',
          page_paragraph: data.page_paragraph || '',
          what_we_offer_title: data.what_we_offer?.title || '',
          what_we_offer_paragraph: data.what_we_offer?.paragraph || '',
          what_we_offer_items: data.what_we_offer?.items || ['', '', '', '', '', ''],
          how_it_works_title: data.how_it_works?.title || '',
          how_it_works_paragraph: data.how_it_works?.paragraph || '',
          how_it_works_steps: data.how_it_works?.steps || [],
          benefits_title: data.benefits?.title || '',
          benefits_items: data.benefits?.items || ['', '', '', '', ''],
          benefits_image: null,
          benefits_image_preview: data.benefits?.image_path || null,
          use_cases_title: data.use_cases?.title || '',
          use_cases_paragraph: data.use_cases?.paragraph || '',
          use_cases_items: data.use_cases?.items || ['', '', '', '', '', ''],
          testimonials: data.testimonials || [],
          faqs_title: data.faqs?.title || '',
          faqs: data.faqs?.items || []
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load services page data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  // How It Works Steps Management (strings only)
  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      how_it_works_steps: [...prev.how_it_works_steps, '']
    }));
  };

  const updateStep = (index, value) => {
    setFormData(prev => ({
      ...prev,
      how_it_works_steps: prev.how_it_works_steps.map((step, i) => 
        i === index ? value : step
      )
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      how_it_works_steps: prev.how_it_works_steps.filter((_, i) => i !== index)
    }));
  };

  // Benefits Image Management
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          benefits_image: file,
          benefits_image_preview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      benefits_image: null,
      benefits_image_preview: null
    }));
  };

  // Testimonials Management
  const addTestimonial = () => {
    setFormData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, {
        message: '',
        photo: null,
        photo_preview: null,
        name: '',
        role: ''
      }]
    }));
  };

  const updateTestimonial = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.map((testimonial, i) => 
        i === index ? { ...testimonial, [field]: value } : testimonial
      )
    }));
  };

  const handleTestimonialImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError('Image size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          testimonials: prev.testimonials.map((testimonial, i) => 
            i === index ? { ...testimonial, photo: file, photo_preview: reader.result } : testimonial
          )
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeTestimonial = (index) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  // FAQs Management
  const addFAQ = () => {
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const updateFAQ = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) => 
        i === index ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const removeFAQ = (index) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.page_title.trim() || !formData.page_paragraph.trim()) {
      setError('Please fill in page title and paragraph.');
      return;
    }

    // Validate What We Offer (strictly 6)
    if (formData.what_we_offer_items.length !== 6 || formData.what_we_offer_items.some(offer => !offer.trim())) {
      setError('Please fill in all 6 offerings in "What We Offer" section.');
      return;
    }

    // Validate How It Works (at least 1 step)
    if (formData.how_it_works_steps.length === 0 || formData.how_it_works_steps.some(step => !step.trim())) {
      setError('Please add at least one step in "How It Works" section.');
      return;
    }

    // Validate Benefits (1-5 items)
    const filledBenefits = formData.benefits_items.filter(b => b.trim());
    if (filledBenefits.length === 0 || filledBenefits.length > 5) {
      setError('Please add 1-5 benefits.');
      return;
    }

    // Validate Use Cases (1-6 items)
    const filledUseCases = formData.use_cases_items.filter(c => c.trim());
    if (filledUseCases.length === 0 || filledUseCases.length > 6) {
      setError('Please add 1-6 use cases.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const submitData = new FormData();
      
      // Basic fields
      submitData.append('page_title', formData.page_title);
      submitData.append('page_paragraph', formData.page_paragraph);
      
      // What We Offer
      submitData.append('what_we_offer_title', formData.what_we_offer_title);
      submitData.append('what_we_offer_paragraph', formData.what_we_offer_paragraph);
      submitData.append('what_we_offer_items', JSON.stringify(formData.what_we_offer_items));
      
      // How It Works
      submitData.append('how_it_works_title', formData.how_it_works_title);
      submitData.append('how_it_works_paragraph', formData.how_it_works_paragraph);
      submitData.append('how_it_works_steps', JSON.stringify(formData.how_it_works_steps));
      
      // Benefits
      submitData.append('benefits_title', formData.benefits_title);
      submitData.append('benefits_items', JSON.stringify(filledBenefits));
      if (formData.benefits_image) {
        submitData.append('benefits_image', formData.benefits_image);
      }

      // Use Cases
      submitData.append('use_cases_title', formData.use_cases_title);
      submitData.append('use_cases_paragraph', formData.use_cases_paragraph);
      submitData.append('use_cases_items', JSON.stringify(filledUseCases));
      
      // Testimonials
      const testimonialsData = formData.testimonials.map(t => ({
        message: t.message,
        name: t.name,
        role: t.role
      }));
      submitData.append('testimonials', JSON.stringify(testimonialsData));
      
      formData.testimonials.forEach((testimonial, index) => {
        if (testimonial.photo) {
          submitData.append(`testimonial_photo_${index}`, testimonial.photo);
        }
      });

      // FAQs
      submitData.append('faqs_title', formData.faqs_title);
      submitData.append('faqs', JSON.stringify(formData.faqs));

      const response = existingData 
        ? await servicesPageAPI.update(submitData)
        : await servicesPageAPI.create(submitData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Services page updated successfully!' : 'Services page created successfully!');
        setExistingData(response.data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save services page';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !existingData) {
    return (
      <ServicesPageContainer>
        <MainContent>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <LoadingSpinner />
            <p style={{ color: '#64748B', fontSize: '1rem' }}>Loading services page data...</p>
          </div>
        </MainContent>
      </ServicesPageContainer>
    );
  }

  return (
    <ServicesPageContainer>
      <FloatingElement delay="0s" />
      <FloatingElement delay="3s" />
      <FloatingElement delay="6s" />

      <MainContent>
        <SectionHeader>
          <Title>Services Page</Title>
          <Subtitle>
            Manage your comprehensive services page with offerings, benefits, use cases, and FAQs.
          </Subtitle>
        </SectionHeader>

        {error && (
          <ErrorMessage>
            <AlertCircle size={20} />
            {error}
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Page Headers */}
          <FormSection>
            <SectionTitle>Page Information</SectionTitle>
            
            <InputGroup>
              <Label htmlFor="page_title">Page Title *</Label>
              <Input
                id="page_title"
                type="text"
                value={formData.page_title}
                onChange={(e) => handleInputChange('page_title', e.target.value)}
                placeholder="e.g., Our Premium Services"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="page_paragraph">Page Paragraph *</Label>
              <TextArea
                id="page_paragraph"
                value={formData.page_paragraph}
                onChange={(e) => handleInputChange('page_paragraph', e.target.value)}
                placeholder="Describe your services..."
                rows={4}
                required
              />
            </InputGroup>
          </FormSection>

          {/* What We Offer */}
          <FormSection>
            <SectionTitle>
              What Our Service Offers
              <Badge>Strictly 6 Offerings</Badge>
            </SectionTitle>

            <InputGroup>
              <Label htmlFor="what_we_offer_title">Section Title *</Label>
              <Input
                id="what_we_offer_title"
                type="text"
                value={formData.what_we_offer_title}
                onChange={(e) => handleInputChange('what_we_offer_title', e.target.value)}
                placeholder="e.g., What We Offer"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="what_we_offer_paragraph">Section Paragraph *</Label>
              <TextArea
                id="what_we_offer_paragraph"
                value={formData.what_we_offer_paragraph}
                onChange={(e) => handleInputChange('what_we_offer_paragraph', e.target.value)}
                placeholder="Introduce what you offer..."
                rows={3}
                required
              />
            </InputGroup>

            <Label>Offerings (Exactly 6 Required) *</Label>
            <Grid>
              {formData.what_we_offer_items.map((offering, index) => (
                <ItemCard key={index}>
                  <ItemNumber>{index + 1}</ItemNumber>
                  <TextArea
                    value={offering}
                    onChange={(e) => handleArrayChange('what_we_offer_items', index, e.target.value)}
                    placeholder={`Offering ${index + 1}`}
                    rows={2}
                    required
                  />
                </ItemCard>
              ))}
            </Grid>
          </FormSection>

          {/* How It Works */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>How The Service Works</SectionTitle>
              <AddButton type="button" onClick={addStep}>
                <Plus size={16} />
                Add Step
              </AddButton>
            </div>

            <InputGroup>
              <Label htmlFor="how_it_works_title">Section Title *</Label>
              <Input
                id="how_it_works_title"
                type="text"
                value={formData.how_it_works_title}
                onChange={(e) => handleInputChange('how_it_works_title', e.target.value)}
                placeholder="e.g., How It Works"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="how_it_works_paragraph">Section Paragraph *</Label>
              <TextArea
                id="how_it_works_paragraph"
                value={formData.how_it_works_paragraph}
                onChange={(e) => handleInputChange('how_it_works_paragraph', e.target.value)}
                placeholder="Explain the process..."
                rows={3}
                required
              />
            </InputGroup>

            {formData.how_it_works_steps.length === 0 ? (
              <EmptyState>
                <Package size={48} style={{ opacity: 0.3 }} />
                <EmptyStateText>No steps added yet. Click "Add Step" to get started.</EmptyStateText>
              </EmptyState>
            ) : (
              <>
                <Grid>
                  {formData.how_it_works_steps.map((step, index) => (
                    <ItemCard key={index}>
                      <CardHeader>
                        <ItemNumber>Step {index + 1}</ItemNumber>
                        <CardActions>
                          <ActionButton 
                            type="button" 
                            onClick={() => removeStep(index)}
                            variant="danger"
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </CardActions>
                      </CardHeader>

                      <InputGroup>
                        <Label>Step Description *</Label>
                        <TextArea
                          value={step}
                          onChange={(e) => updateStep(index, e.target.value)}
                          placeholder="Describe this step..."
                          rows={3}
                          required
                        />
                      </InputGroup>
                    </ItemCard>
                  ))}
                </Grid>
                
                {/* Add Step button at the bottom for better UX */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                  <AddButton type="button" onClick={addStep}>
                    <Plus size={16} />
                    Add Another Step
                  </AddButton>
                </div>
              </>
            )}
          </FormSection>

          {/* Benefits */}
          <FormSection>
            <SectionTitle>
              Benefits of Our Service
              <Badge>Max 5 Benefits</Badge>
            </SectionTitle>

            <InputGroup>
              <Label htmlFor="benefits_title">Section Title *</Label>
              <Input
                id="benefits_title"
                type="text"
                value={formData.benefits_title}
                onChange={(e) => handleInputChange('benefits_title', e.target.value)}
                placeholder="e.g., Why Choose Us"
                required
              />
            </InputGroup>

            <Label>Benefits (Up to 5) *</Label>
            <Grid>
              {formData.benefits_items.map((benefit, index) => (
                <ItemCard key={index}>
                  <ItemNumber>{index + 1}</ItemNumber>
                  <TextArea
                    value={benefit}
                    onChange={(e) => handleArrayChange('benefits_items', index, e.target.value)}
                    placeholder={`Benefit ${index + 1}`}
                    rows={3}
                  />
                </ItemCard>
              ))}
            </Grid>

            <InputGroup>
              <Label>Benefits Section Image</Label>
              {formData.benefits_image_preview ? (
                <ImageUploadContainer>
                  <ImagePreview src={formData.benefits_image_preview} alt="Benefits" />
                  <RemoveImageButton type="button" onClick={removeImage}>
                    <X size={16} />
                    Remove Image
                  </RemoveImageButton>
                </ImageUploadContainer>
              ) : (
                <ImageUploadButton type="button" onClick={() => document.getElementById('benefits_image').click()}>
                  <Upload size={20} />
                  Upload Image
                </ImageUploadButton>
              )}
              <input
                id="benefits_image"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
            </InputGroup>
          </FormSection>

          {/* Common Use Cases */}
          <FormSection>
            <SectionTitle>
              Common Use Cases
              <Badge>Max 6 Use Cases</Badge>
            </SectionTitle>

            <InputGroup>
              <Label htmlFor="use_cases_title">Section Title *</Label>
              <Input
                id="use_cases_title"
                type="text"
                value={formData.use_cases_title}
                onChange={(e) => handleInputChange('use_cases_title', e.target.value)}
                placeholder="e.g., Common Use Cases"
                required
              />
            </InputGroup>

            <InputGroup>
              <Label htmlFor="use_cases_paragraph">Section Paragraph *</Label>
              <TextArea
                id="use_cases_paragraph"
                value={formData.use_cases_paragraph}
                onChange={(e) => handleInputChange('use_cases_paragraph', e.target.value)}
                placeholder="Introduce use cases..."
                rows={3}
                required
              />
            </InputGroup>

            <Label>Use Cases (Up to 6) *</Label>
            <Grid>
              {formData.use_cases_items.map((useCase, index) => (
                <ItemCard key={index}>
                  <ItemNumber>{index + 1}</ItemNumber>
                  <TextArea
                    value={useCase}
                    onChange={(e) => handleArrayChange('use_cases_items', index, e.target.value)}
                    placeholder={`Use case ${index + 1}`}
                    rows={2}
                  />
                </ItemCard>
              ))}
            </Grid>
          </FormSection>

          {/* Testimonials */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>What Our Clients Say</SectionTitle>
              <AddButton type="button" onClick={addTestimonial}>
                <Plus size={16} />
                Add Testimonial
              </AddButton>
            </div>

            {formData.testimonials.length === 0 ? (
              <EmptyState>
                <MessageSquare size={48} style={{ opacity: 0.3 }} />
                <EmptyStateText>No testimonials added yet. Click "Add Testimonial" to get started.</EmptyStateText>
              </EmptyState>
            ) : (
              <>
                <Grid>
                  {formData.testimonials.map((testimonial, index) => (
                    <TestimonialCard key={index}>
                      <CardHeader>
                        <CardTitle>Testimonial {index + 1}</CardTitle>
                        <CardActions>
                          <ActionButton 
                            type="button" 
                            onClick={() => removeTestimonial(index)}
                            variant="danger"
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </CardActions>
                      </CardHeader>

                      <InputGroup>
                        <Label>Message *</Label>
                        <TextArea
                          value={testimonial.message}
                          onChange={(e) => updateTestimonial(index, 'message', e.target.value)}
                          placeholder="Client's testimonial message..."
                          rows={3}
                          required
                        />
                      </InputGroup>

                      <InputGroup>
                        <Label>Client Photo</Label>
                        {testimonial.photo_preview ? (
                          <ImageUploadContainer>
                            <TestimonialImage src={testimonial.photo_preview} alt={testimonial.name} />
                            <RemoveImageButton 
                              type="button" 
                              onClick={() => updateTestimonial(index, 'photo', null) || updateTestimonial(index, 'photo_preview', null)}
                            >
                              <X size={16} />
                              Remove
                            </RemoveImageButton>
                          </ImageUploadContainer>
                        ) : (
                          <ImageUploadButton 
                            type="button" 
                            onClick={() => document.getElementById(`testimonial_photo_${index}`).click()}
                          >
                            <Upload size={20} />
                            Upload Photo
                          </ImageUploadButton>
                        )}
                        <input
                          id={`testimonial_photo_${index}`}
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => handleTestimonialImageUpload(index, e)}
                        />
                      </InputGroup>

                      <InputGroup>
                        <Label>Name *</Label>
                        <Input
                          type="text"
                          value={testimonial.name}
                          onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                          placeholder="Client's name"
                          required
                        />
                      </InputGroup>

                      <InputGroup>
                        <Label>Role *</Label>
                        <Input
                          type="text"
                          value={testimonial.role}
                          onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                          placeholder="e.g., CEO at Company"
                          required
                        />
                      </InputGroup>
                    </TestimonialCard>
                  ))}
                </Grid>
                
                {/* Add Testimonial button at the bottom for better UX */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                  <AddButton type="button" onClick={addTestimonial}>
                    <Plus size={16} />
                    Add Another Testimonial
                  </AddButton>
                </div>
              </>
            )}
          </FormSection>

          {/* FAQs */}
          <FormSection>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <SectionTitle>Frequently Asked Questions</SectionTitle>
              <AddButton type="button" onClick={addFAQ}>
                <Plus size={16} />
                Add FAQ
              </AddButton>
            </div>

            <InputGroup>
              <Label htmlFor="faqs_title">Section Title *</Label>
              <Input
                id="faqs_title"
                type="text"
                value={formData.faqs_title}
                onChange={(e) => handleInputChange('faqs_title', e.target.value)}
                placeholder="e.g., Frequently Asked Questions"
                required
              />
            </InputGroup>

            {formData.faqs.length === 0 ? (
              <EmptyState>
                <HelpCircle size={48} style={{ opacity: 0.3 }} />
                <EmptyStateText>No FAQs added yet. Click "Add FAQ" to get started.</EmptyStateText>
              </EmptyState>
            ) : (
              <>
                {formData.faqs.map((faq, index) => (
                  <FAQCard key={index}>
                    <CardHeader>
                      <CardTitle>FAQ {index + 1}</CardTitle>
                      <CardActions>
                        <ActionButton 
                          type="button" 
                          onClick={() => removeFAQ(index)}
                          variant="danger"
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      </CardActions>
                    </CardHeader>

                    <InputGroup>
                      <Label>Question *</Label>
                      <Input
                        type="text"
                        value={faq.question}
                        onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                        placeholder="Enter the question..."
                        required
                      />
                    </InputGroup>

                    <InputGroup>
                      <Label>Answer *</Label>
                      <TextArea
                        value={faq.answer}
                        onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                        placeholder="Enter the answer..."
                        rows={3}
                        required
                      />
                    </InputGroup>
                  </FAQCard>
                ))}
                
                {/* Add FAQ button at the bottom for better UX */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                  <AddButton type="button" onClick={addFAQ}>
                    <Plus size={16} />
                    Add Another FAQ
                  </AddButton>
                </div>
              </>
            )}
          </FormSection>

          <SubmitButton type="submit" disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner />
                {existingData ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save size={20} />
                {existingData ? 'Update Services Page' : 'Create Services Page'}
              </>
            )}
          </SubmitButton>
        </Form>

        {existingData && (
          <PreviewSection>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewCard>
              <h2 style={{ color: '#2B3857', marginBottom: '0.5rem' }}>
                {formData.page_title}
              </h2>
              <p style={{ color: '#64748B', marginBottom: '2rem', lineHeight: '1.6' }}>
                {formData.page_paragraph}
              </p>
              
              <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                <div>
                  <strong style={{ color: '#2B3857' }}>Offerings:</strong> 6
                </div>
                <div>
                  <strong style={{ color: '#2B3857' }}>Steps:</strong> {formData.how_it_works_steps.length}
                </div>
                <div>
                  <strong style={{ color: '#2B3857' }}>Benefits:</strong> {formData.benefits_items.filter(b => b.trim()).length}
                </div>
                <div>
                  <strong style={{ color: '#2B3857' }}>Use Cases:</strong> {formData.use_cases_items.filter(c => c.trim()).length}
                </div>
                <div>
                  <strong style={{ color: '#2B3857' }}>Testimonials:</strong> {formData.testimonials.length}
                </div>
                <div>
                  <strong style={{ color: '#2B3857' }}>FAQs:</strong> {formData.faqs.length}
                </div>
              </div>
            </PreviewCard>
          </PreviewSection>
        )}

      </MainContent>
    </ServicesPageContainer>
  );
};

export default ServicesPage;

